// ─────────────────────────────────────────────────────────────────────────────
//  subFieldRowFormat — read-side formatting for a repeat field whose value is a
//  LIST OF ROW OBJECTS described by `field.subFields` (e.g. a trainer's
//  availability: [{ days, startTime, endTime }, …]).
//
//  Without this, the detail view prints each row's raw stored values, in the
//  order the keys happened to be stored:
//
//    Wednesday - 2026-09-16T15:30:00.000Z - 2026-09-16T11:30:00.000Z
//
//  With it, each row is described by the SAME subField config the add/edit
//  forms render from, so the read side speaks the form's language:
//
//    Wednesday - 16 Sep 2026 - 05:00 PM - 09:00 PM
//
//  OPT-IN, per field, from Admin → Detail Groups (renderOptions):
//
//    formatRows       true to enable. Anything else leaves the field's output
//                     exactly as it was, so fields already live elsewhere
//                     (assessment options, course lessons) are untouched until
//                     an admin chooses otherwise.
//    subFieldFormats  { <subFieldKey>: <dayjs format> } — per-part override,
//                     e.g. { startTime: 'DD MMM YYYY - hh:mm A', endTime: 'hh:mm A' }
//
//  Contract (stable from first release): the two keys above, and the rule that
//  parts follow subFields ORDER, empty parts are skipped, and a value that cannot
//  be formatted is shown as stored rather than dropped.
//
//  Pure: no React, no DOM — so it is unit-testable in isolation.
// ─────────────────────────────────────────────────────────────────────────────
import { formatApp } from '../../services/timezone';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}|$)/;
const TIME_TYPES = new Set(['time']);
const DATE_TYPES = new Set(['date']);
const DATETIME_TYPES = new Set(['datetime', 'date-time', 'datetime-local']);
const OPTION_TYPES = new Set(['select', 'radio', 'multiselect', 'checkbox-group', 'checkboxgroup']);
const HTML_TYPES = new Set(['text-editor', 'richtext', 'rich-text', 'html', 'editor']);

const truthy = (v) => v === true || v === 1 || v === '1' || v === 'true';

/** A part with nothing to say. 0 and false are REAL values and are kept. */
const isAbsent = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/**
 * rowsEnabled — should this field's rows be formatted?
 * True only when the admin switched it on AND there is a subField config to
 * format by. A missing or non-boolean `formatRows` is treated as off.
 */
export function rowsEnabled(field) {
  return field?.renderOptions?.formatRows === true
    && Array.isArray(field?.subFields)
    && field.subFields.length > 0;
}

function optionLabel(subField, raw) {
  const options = Array.isArray(subField?.options) ? subField.options : [];
  const match = options.find((o) => String(o?.value ?? '') === String(raw));
  return match?.label != null && String(match.label).trim() !== ''
    ? String(match.label).trim()
    : String(raw);
}

function formatInstant(raw, format, defaults) {
  const source = raw?.$date ?? raw;
  const shown = formatApp(source, format, defaults);
  return shown || String(source);
}

function plainText(html) {
  return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * formatPart — one subField's value as display text, or '' to skip it.
 */
export function formatPart(subField, raw, renderOptions = {}, defaults = {}) {
  if (isAbsent(raw)) return '';
  const key = subField?.field;
  const type = String(subField?.type ?? '').trim().toLowerCase();
  const override = typeof renderOptions?.subFieldFormats?.[key] === 'string'
    && renderOptions.subFieldFormats[key].trim() !== ''
    ? renderOptions.subFieldFormats[key]
    : '';

  if (TIME_TYPES.has(type)) return formatInstant(raw, override || defaults.timeFormat || 'hh:mm A', defaults);
  if (DATE_TYPES.has(type)) return formatInstant(raw, override || defaults.dateFormat || 'DD MMM YYYY', defaults);
  if (DATETIME_TYPES.has(type)) {
    return formatInstant(raw, override || defaults.dateTimeFormat || 'DD MMM YYYY hh:mm A', defaults);
  }

  // A reference ({ id, value }) reads as its display value.
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw) {
    return formatPart(subField, raw.value, renderOptions, defaults);
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => formatPart(subField, item, renderOptions, defaults))
      .filter((s) => s !== '')
      .join(', ');
  }

  // Any other object (a stored file container, a nested structure) has no
  // single honest text form; skip it rather than print "[object Object]".
  if (raw && typeof raw === 'object') return '';

  if (typeof raw === 'boolean') {
    return raw ? (defaults.booleanTrueLabel || 'Yes') : (defaults.booleanFalseLabel || 'No');
  }

  if (OPTION_TYPES.has(type)) return optionLabel(subField, raw);
  if (HTML_TYPES.has(type)) return plainText(raw);

  // An explicit format on an otherwise untyped part that stores a date.
  if (override && typeof raw === 'string' && ISO_DATE_RE.test(raw.trim())) {
    return formatInstant(raw, override, defaults);
  }
  return String(raw);
}

/**
 * formatSubFieldRow — one row object as display text, parts in subFields order.
 */
export function formatSubFieldRow(row, subFields, renderOptions = {}, defaults = {}) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return '';
  const separator = typeof defaults.separator === 'string' && defaults.separator !== ''
    ? defaults.separator
    : ' - ';
  return (subFields ?? [])
    // A subField hidden on the form is hidden here too.
    .filter((sf) => sf?.field && (sf.show === undefined || truthy(sf.show)))
    .map((sf) => {
      const raw = row[sf.field] !== undefined ? row[sf.field] : (sf.payloadKey ? row[sf.payloadKey] : undefined);
      return formatPart(sf, raw, renderOptions, defaults);
    })
    .filter((part) => part !== '')
    .join(separator);
}

/**
 * formatSubFieldRows — the whole value as one display string per row, or null
 * when this field does not opt in (callers then keep their existing path).
 * Non-object items are left to the caller's normal handling by returning null,
 * so a value that is not actually a list of rows is never reshaped.
 */
export function formatSubFieldRows(field, value, defaults = {}) {
  if (!rowsEnabled(field) || !Array.isArray(value)) return null;
  if (!value.every((row) => row && typeof row === 'object' && !Array.isArray(row))) return null;
  return value
    .map((row) => formatSubFieldRow(row, field.subFields, field.renderOptions, defaults))
    .filter((text) => text !== '');
}
